/**
 * Card-scheme profiles for the deposit card form.
 *
 * `STANDARD` covers Visa/Mastercard (16 digits, 3-digit CVV). `AMEX` relaxes the
 * constraints for American Express (15 digits grouped 4-6-5, 4-digit CVV, 34/37
 * prefix). The scheme is detected from the card number, and AMEX is only offered
 * in markets that accept it (see `getCardSchemeForNumber`).
 */

enum CardScheme {
  STANDARD = 'standard',
  AMEX = 'amex',
}

type CardSchemeProfile = {
  /** Number of digits a valid card number of this scheme has. */
  cardDigits: number;
  /** Number of digits in the security code (CVV/CID). */
  cvvDigits: number;
  /** Digit group sizes used to space the card number as the user types. */
  groups: readonly number[];
  /** Prefix a card number must match, if the scheme is prefix-constrained. */
  prefix?: RegExp;
};

const CARD_SCHEME_PROFILES: Record<CardScheme, CardSchemeProfile> = {
  [CardScheme.STANDARD]: { cardDigits: 16, cvvDigits: 3, groups: [4, 4, 4, 4] },
  [CardScheme.AMEX]: { cardDigits: 15, cvvDigits: 4, groups: [4, 6, 5], prefix: /^3[47]/ },
};

const AMEX_PREFIX = CARD_SCHEME_PROFILES[CardScheme.AMEX].prefix as RegExp;

/**
 * Detects the card scheme from the (possibly spaced) card number. American Express
 * (34/37 prefix) is only detected in markets that accept it; elsewhere the number is
 * treated as STANDARD, so a 15-digit AMEX number fails the 16-digit rule and is rejected.
 */
function getCardSchemeForNumber(cardNumber: string, acceptsAmexCards: boolean): CardScheme {
  const digits = cardNumber.replace(/\D/g, '');
  if (acceptsAmexCards && AMEX_PREFIX.test(digits)) return CardScheme.AMEX;
  return CardScheme.STANDARD;
}

/**
 * Groups a run of digits into space-separated blocks.
 * e.g. groupDigits('374245455400126', [4, 6, 5]) -> '3742 454554 00126'
 */
function groupDigits(digits: string, groups: readonly number[]): string {
  const blocks: string[] = [];
  let index = 0;

  for (const size of groups) {
    if (index >= digits.length) break;
    blocks.push(digits.slice(index, index + size));
    index += size;
  }

  // Anything beyond the defined groups (shouldn't normally happen once capped) is appended as-is.
  if (index < digits.length) {
    blocks.push(digits.slice(index));
  }

  return blocks.join(' ');
}

/**
 * Given a formatted (space-grouped) value and a number of digits that should sit
 * before the caret, returns the string index to place the caret at — so editing a
 * digit mid-number keeps the cursor on the same digit boundary instead of jumping
 * to the end when the value is re-grouped.
 */
function caretPositionForDigit(formattedValue: string, digitsBeforeCaret: number): number {
  if (digitsBeforeCaret <= 0) return 0;

  let seenDigits = 0;
  for (let i = 0; i < formattedValue.length; i++) {
    if (/\d/.test(formattedValue.charAt(i))) {
      seenDigits += 1;
      if (seenDigits === digitsBeforeCaret) return i + 1;
    }
  }

  return formattedValue.length;
}

export { CARD_SCHEME_PROFILES, CardScheme, caretPositionForDigit, getCardSchemeForNumber, groupDigits };
export type { CardSchemeProfile };
